import { useTranslation } from "react-i18next";
import { useCmsText } from "../../hooks/useCmsText";
import { useSiteConfig } from "../../config/SiteConfigContext";
import { PrivacyNotice } from "../../components/PrivacyNotice";
import { ConsentCheckbox } from "../../components/ConsentCheckbox";
import { stripHtml } from "../../utils/html";
import type { ComposerApi } from "../useRequestComposer";
import { Glyph } from "./Icons";

const ITEM_KEY = {
  request: "itemRequest", name: "itemName", email: "itemEmail", phone: "itemPhone", shorter: "itemShorter", date: "itemDate",
} as const;

/**
 * Sotto la cartolina, nell'ordine che il testo legale presuppone: modifica,
 * cosa manca, cosa succede premendo «Invia», informativa A (nessuna casella),
 * consenso E (facoltativo, sempre vuoto all'inizio), poi l'unico pulsante
 * d'invio, con il testo del CMS.
 *
 * Il consenso E non entra in «Manca/Pronta», nei francobolli o nel timbro.
 */
export function FinalBlock({ api }: { api: ComposerApi }) {
  const { t: tLegal } = useTranslation();
  const ct = useCmsText("contact");
  const cfg = useSiteConfig();
  const { t, state, dispatch, missing, sending, steps } = api;
  const d = state.draft;
  const send = stripHtml(ct("send"));
  const items = missing.map((m) => t(`contact.guide.ready.${ITEM_KEY[m]}`)).join(" · ");
  const contactEmail = cfg.contact_email || "info@granborgo.com";

  return (
    <div className="rq-final" hidden={state.step !== 3} data-gb="riepilogo">
      <div className="rq-edits">
        <button type="button" className="rq-link" onClick={() => api.goTo(1, true)} data-gb="modifica-argomento">
          {t("contact.guide.edit.topic")}
        </button>
        {steps.includes(2) && (
          <button type="button" className="rq-link" onClick={() => api.goTo(2, true)} data-gb="modifica-dettagli">
            {t("contact.guide.edit.details")}
          </button>
        )}
      </div>

      <p className="rq-ready" data-ok={missing.length ? undefined : ""} data-gb="pronta">
        <Glyph name={missing.length ? "info" : "check"} size={16} />
        <span>{missing.length ? t("contact.guide.ready.missing", { items }) : t("contact.guide.ready.ok", { send })}</span>
      </p>

      <p className="rq-after">{t("contact.guide.after", { send })}</p>

      <div className="rq-legal">
        <PrivacyNotice variant="contact" style={{ color: "var(--rq-ink-2, rgba(28,75,59,.86))" }} />
        <ConsentCheckbox
          name="marketing_consent"
          label={tLegal("consent.marketing")}
          checked={d.marketingConsent}
          onChange={(value) => dispatch({ type: "consent", value })}
        />
      </div>

      {/* Sempre montato e vuoto finché serve: l'inserimento del testo viene annunciato. */}
      <div className="rq-alert" role="alert" data-gb="errore-invio">
        {state.phase === "error" && (
          <>
            <p>{ct("errorMsg")}</p>
            <p>{t("contact.guide.errorHint", { email: contactEmail })}</p>
          </>
        )}
      </div>

      <button
        ref={api.setEl("submit")}
        type="submit"
        className="rq-btn rq-btn--primary rq-btn--block"
        data-gb="invia"
        aria-disabled={sending || undefined}
        onClick={api.onSubmitClick}
      >
        {sending && <span className="rq-spin" aria-hidden="true" />}
        {send}
      </button>

      <div>
        <button type="button" className="rq-link" onClick={api.back}>
          <Glyph name="prev" size={14} />
          {t("contact.guide.nav.back")}
        </button>
      </div>
    </div>
  );
}
